import React, { useEffect, useState } from 'react';
import api, { projectAPI, taskAPI, userAPI } from '../services/api';
import { FiTrendingUp, FiDollarSign, FiCheckSquare, FiUsers, FiClock, FiBarChart2 } from 'react-icons/fi';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Analytics() {
  const [data, setData] = useState({ projects:[], tasks:[], users:[], salaries:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pR, tR, uR] = await Promise.all([projectAPI.getAll(), taskAPI.getAll(), userAPI.getAll()]);
        let salaries = [];
        try { const sR = await api.get('/salary/all'); salaries = sR.data.data||[]; } catch {}
        setData({ projects: pR.data.projects||[], tasks: tR.data.tasks||[], users: uR.data.users||[], salaries });
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'60vh'}}><div style={{width:40,height:40,border:'3px solid #e2e8f0',borderTop:'3px solid #6366f1',borderRadius:'50%',animation:'spin 1s linear infinite'}}/></div>;

  const { projects, tasks, users, salaries } = data;
  const totalRevenue = salaries.filter(s=>s.status==='paid').reduce((sum,s)=>sum+(s.netSalary||0),0);
  const completedProjects = projects.filter(p=>p.status==='completed').length;
  const completedTasks    = tasks.filter(t=>t.status==='completed').length;
  const teamMembers       = users.filter(u=>u.role!=='client').length;

  // Task by status
  const taskByStatus = ['todo','in-progress','review','completed'].map(s=>({
    label:s, count:tasks.filter(t=>t.status===s).length,
    color:s==='completed'?'#22c55e':s==='in-progress'?'#f59e0b':s==='review'?'var(--pm-primary)':'#94a3b8'
  }));

  // Projects by type
  const types = [...new Set(projects.map(p=>p.projectType).filter(Boolean))];
  const projectByType = types.map(t=>({ label:t, count:projects.filter(p=>p.projectType===t).length }));

  // Task priority breakdown
  const taskByPriority = ['low','medium','high','critical'].map(p=>({
    label:p, count:tasks.filter(t=>t.priority===p).length,
    color:p==='critical'?'#7c3aed':p==='high'?'#ef4444':p==='medium'?'#f59e0b':'#22c55e'
  }));

  const S = {
    page:{maxWidth:1200,margin:'0 auto',fontFamily:"'Sora',sans-serif"},
    card:{background:'#fff',borderRadius:16,padding:24,boxShadow:'0 2px 12px #0000000a',border:'1px solid #f1f5f9'},
    h3:{margin:'0 0 20px',fontSize:15,fontWeight:700,color:'#0f172a'},
  };

  const BarChart = ({ items, maxVal }) => (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {items.map(item=>(
        <div key={item.label} style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:90,fontSize:12,color:'#64748b',textAlign:'right',textTransform:'capitalize',flexShrink:0}}>{item.label}</div>
          <div style={{flex:1,height:24,background:'#f1f5f9',borderRadius:6,overflow:'hidden',position:'relative'}}>
            <div style={{height:'100%',width:`${maxVal?((item.count/maxVal)*100):0}%`,background:item.color||'var(--pm-primary)',borderRadius:6,transition:'width .8s cubic-bezier(.4,0,.2,1)',display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:8}}>
              {item.count>0&&<span style={{color:'#fff',fontSize:11,fontWeight:700}}>{item.count}</span>}
            </div>
          </div>
          <div style={{width:28,textAlign:'right',fontSize:13,fontWeight:700,color:'#0f172a'}}>{item.count}</div>
        </div>
      ))}
    </div>
  );

  const DonutSlice = ({ items }) => {
    const total = items.reduce((s,i)=>s+i.count,0);
    let angle = -90;
    return (
      <div style={{display:'flex',gap:24,alignItems:'center',flexWrap:'wrap'}}>
        <svg width={140} height={140} viewBox="0 0 140 140">
          {total===0?<circle cx={70} cy={70} r={50} fill="none" stroke="#f1f5f9" strokeWidth={22}/>:
          items.map((item,i)=>{
            const pct = item.count/total; const sweep = pct*360;
            const r=50; const cx=70,cy=70;
            const startAngle = (angle*Math.PI)/180; angle+=sweep;
            const endAngle = (angle*Math.PI)/180;
            const x1=cx+r*Math.cos(startAngle); const y1=cy+r*Math.sin(startAngle);
            const x2=cx+r*Math.cos(endAngle);   const y2=cy+r*Math.sin(endAngle);
            const large = sweep>180?1:0;
            return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`} fill={item.color||'var(--pm-primary)'} opacity={.85}/>;
          })}
          <circle cx={70} cy={70} r={32} fill="#fff"/>
          <text x={70} y={74} textAnchor="middle" style={{fontSize:18,fontWeight:800,fill:'#0f172a'}}>{total}</text>
        </svg>
        <div style={{flex:1}}>
          {items.map(item=>(
            <div key={item.label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,fontSize:13}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div style={{width:10,height:10,borderRadius:'50%',background:item.color||'var(--pm-primary)',flexShrink:0}}/>
                <span style={{color:'#475569',textTransform:'capitalize'}}>{item.label}</span>
              </div>
              <span style={{fontWeight:700,color:'#0f172a'}}>{total?Math.round((item.count/total)*100):0}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&display=swap'); @keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{marginBottom:28}}>
        <h1 style={{margin:0,fontSize:28,fontWeight:800,color:'#0f172a'}}>📊 Analytics</h1>
        <p style={{margin:'4px 0 0',color:'#64748b'}}>Overview of your project management activity</p>
      </div>

      {/* KPI Strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginBottom:24}}>
        {[
          {i:<FiBarChart2/>,l:'Total Projects',v:projects.length,sub:`${completedProjects} completed`,c:'var(--pm-primary)'},
          {i:<FiCheckSquare/>,l:'Total Tasks',v:tasks.length,sub:`${completedTasks} completed`,c:'#22c55e'},
          {i:<FiUsers/>,l:'Team Members',v:teamMembers,sub:`${users.filter(u=>u.role==='client').length} clients`,c:'#0ea5e9'},
          {i:<FiClock/>,l:'Active Tasks',v:tasks.filter(t=>t.status==='in-progress').length,sub:'in progress',c:'#f59e0b'},
          {i:<FiDollarSign/>,l:'Total Payroll Paid',v:`₹${totalRevenue.toLocaleString('en-IN')}`,sub:'all time',c:'#10b981'},
          {i:<FiTrendingUp/>,l:'Completion Rate',v:`${tasks.length?Math.round((completedTasks/tasks.length)*100):0}%`,sub:'tasks done',c:'var(--pm-secondary)'},
        ].map(s=>(
          <div key={s.l} style={{...S.card,borderLeft:`4px solid ${s.c}`}}>
            <div style={{color:s.c,fontSize:22,marginBottom:10}}>{s.i}</div>
            <div style={{fontSize:26,fontWeight:800,color:'#0f172a'}}>{s.v}</div>
            <div style={{fontSize:12,color:'#64748b',marginTop:2}}>{s.l}</div>
            <div style={{fontSize:11,color:s.c,marginTop:4,fontWeight:600}}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:20,marginBottom:20}}>
        <div style={S.card}>
          <h3 style={S.h3}>Tasks by Status</h3>
          <BarChart items={taskByStatus} maxVal={Math.max(...taskByStatus.map(i=>i.count),1)}/>
        </div>
        <div style={S.card}>
          <h3 style={S.h3}>Tasks by Priority</h3>
          <DonutSlice items={taskByPriority}/>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:20,marginBottom:20}}>
        <div style={S.card}>
          <h3 style={S.h3}>Projects by Type</h3>
          {projectByType.length===0 ? <div style={{color:'#94a3b8',textAlign:'center',padding:24}}>No data</div>
           : <BarChart items={projectByType.map(i=>({...i,color:'var(--pm-primary)'}))} maxVal={Math.max(...projectByType.map(i=>i.count),1)}/>}
        </div>
        <div style={S.card}>
          <h3 style={S.h3}>Project Status Distribution</h3>
          <DonutSlice items={[
            {label:'Active',count:projects.filter(p=>p.status==='active').length,color:'#22c55e'},
            {label:'Planning',count:projects.filter(p=>p.status==='planning').length,color:'var(--pm-primary)'},
            {label:'On Hold',count:projects.filter(p=>p.status==='on-hold').length,color:'#f59e0b'},
            {label:'Completed',count:projects.filter(p=>p.status==='completed').length,color:'#0ea5e9'},
            {label:'Cancelled',count:projects.filter(p=>p.status==='cancelled').length,color:'#ef4444'},
          ]}/>
        </div>
      </div>

      {/* Recent Projects Table */}
      <div style={S.card}>
        <h3 style={S.h3}>Recent Projects</h3>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead><tr style={{background:'#f8fafc'}}>{['Project','Type','Status','Tasks','Budget'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:'left',fontWeight:700,color:'#64748b',borderBottom:'2px solid #f1f5f9'}}>{h}</th>)}</tr></thead>
          <tbody>
            {projects.slice(0,8).map(p=>(
              <tr key={p._id} style={{borderBottom:'1px solid #f8fafc'}}>
                <td style={{padding:'10px 14px',fontWeight:600}}>{p.name}</td>
                <td style={{padding:'10px 14px',color:'#64748b'}}>{p.projectType||'—'}</td>
                <td style={{padding:'10px 14px'}}><span style={{background:p.status==='completed'?'#d1fae5':p.status==='active'?'#e0f2fe':'#fef3c7',color:p.status==='completed'?'#065f46':p.status==='active'?'#0369a1':'#92400e',padding:'3px 10px',borderRadius:12,fontSize:11,fontWeight:700,textTransform:'uppercase'}}>{p.status}</span></td>
                <td style={{padding:'10px 14px',color:'#64748b'}}>{tasks.filter(t=>t.project===p._id||t.project?._id===p._id).length}</td>
                <td style={{padding:'10px 14px',fontWeight:600,color:'#10b981'}}>{p.budget ? new Intl.NumberFormat('en-US',{style:'currency',currency:p.client?.transactionCurrency||'USD',maximumFractionDigits:2}).format(p.budget) : '—'}</td>
              </tr>
            ))}
            {projects.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>No projects yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
